"use client";


import Link from "next/link";
import { ExplanationResult } from "@/app/error-explainer-tool/types";
import SiteHeader from "@/app/error-explainer-tool/components/SiteHeader";

interface ErrorRow {
  id: string;
  error_message: string;
  language: string;
  slug: string;
  views: number;
  created_at: string;
  explanation: string;
}

interface Props {
  language: string;
  displayName: string;
  errors: ErrorRow[];
  total: number;
  currentPage: number;
  totalPages: number;
  sort: string;
}

const LANGUAGE_COLORS: Record<string, string> = {
  javascript: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
  php: "text-violet-400 bg-violet-400/10 border-violet-400/20",
  python: "text-blue-400 bg-blue-400/10 border-blue-400/20",
  java: "text-orange-400 bg-orange-400/10 border-orange-400/20",
  sql: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  other: "text-zinc-400 bg-zinc-400/10 border-zinc-400/20",
};

const LANGUAGE_ICONS: Record<string, string> = {
  javascript: "JS",
  php: "PHP",
  python: "PY",
  java: "JV",
  sql: "SQL",
};

function getPopularityLabel(views: number): string | null {
  if (views >= 1000) return "Popular";
  if (views >= 250) return "Trending";
  if (views >= 100) return "Rising";
  return null;
}

function getExplanationPreview(explanationJson: string): string {
  try {
    const parsed = JSON.parse(explanationJson) as ExplanationResult;
    return parsed.meaning?.slice(0, 120) || "";
  } catch {
    return "";
  }
}

export default function LanguagePageClient({
  language,
  displayName,
  errors,
  total,
  currentPage,
  totalPages,
  sort,
}: Props) {
  const langColor = LANGUAGE_COLORS[language] || LANGUAGE_COLORS.other;
  const langIcon = LANGUAGE_ICONS[language] || language.toUpperCase().slice(0, 3);

  function buildUrl(params: { sort?: string; page?: number }) {
    const s = params.sort ?? sort;
    const p = params.page ?? currentPage;
    const qs = new URLSearchParams();
    if (s !== "views") qs.set("sort", s);
    if (p > 1) qs.set("page", String(p));
    const str = qs.toString();
    return `/languages/${language}${str ? `?${str}` : ""}`;
  }

  return (
    <div
      className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col"
      style={{ fontFamily: "'Plus Jakarta Sans', 'DM Sans', system-ui, sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #18181b; }
        ::-webkit-scrollbar-thumb { background: #3f3f46; border-radius: 3px; }
      `}</style>

      <SiteHeader />

      <div className="border-b border-zinc-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link
            href="/error-explainer-tool"
            className="flex items-center gap-2 text-zinc-400 hover:text-zinc-100 transition-colors text-sm"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Explainer
          </Link>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${langColor}`} style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            {langIcon}
          </span>
        </div>
      </div>

      <main className="flex-1 w-full max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-zinc-500 mb-6" aria-label="Breadcrumb">
          <Link href="/error-explainer-tool" className="hover:text-zinc-300 transition-colors">Home</Link>
          <span>/</span>
          <span className="text-zinc-400">Browse Errors</span>
          <span>/</span>
          <span className="text-zinc-300">{displayName}</span>
        </nav>

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <span className={`inline-flex items-center text-sm font-semibold px-3 py-1.5 rounded-full border ${langColor}`}>
              {displayName}
            </span>
            <span className="text-zinc-600 text-sm">{total} errors documented</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 leading-tight">
            Common {displayName} Errors and How to Fix Them
          </h1>
          <p className="text-zinc-400 text-base leading-relaxed max-w-2xl">
            Browse all documented {displayName} errors with detailed explanations, root causes, and step-by-step fixes.
          </p>
        </div>

        {/* Sort Controls */}
        <div className="flex items-center gap-2 mb-6">
          <span className="text-zinc-500 text-sm">Sort by:</span>
          <Link
            href={buildUrl({ sort: "views", page: 1 })}
            className={`text-sm px-3 py-1.5 rounded-lg border transition-all ${
              sort !== "newest" ?"bg-cyan-500/10 border-cyan-500/30 text-cyan-400" :"border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300"
            }`}
          >
            Most Viewed
          </Link>
          <Link
            href={buildUrl({ sort: "newest", page: 1 })}
            className={`text-sm px-3 py-1.5 rounded-lg border transition-all ${
              sort === "newest" ?"bg-cyan-500/10 border-cyan-500/30 text-cyan-400" :"border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300"
            }`}
          >
            Newest
          </Link>
        </div>

        {/* Error Cards */}
        {errors.length === 0 ? (
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-12 text-center">
            <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center mx-auto mb-4 ${langColor}`}>
              <span className="text-lg font-bold">{langIcon.slice(0, 1)}</span>
            </div>
            <p className="text-zinc-300 font-medium mb-2">No {displayName} errors yet</p>
            <p className="text-zinc-500 text-sm mb-6">Be the first to document a {displayName} error.</p>
            <Link
              href="/error-explainer-tool"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500 text-zinc-950 font-semibold text-sm hover:bg-cyan-400 transition-colors"
            >
              Explain an Error
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {errors.map((err) => {
              const preview = getExplanationPreview(err.explanation);
              const popularity = getPopularityLabel(err.views);
              return (
                <Link
                  key={err.id}
                  href={`/errors/${err.slug}`}
                  className="group bg-zinc-900 border border-zinc-800 rounded-2xl p-5 hover:border-zinc-600 hover:bg-zinc-800/50 transition-all flex flex-col gap-3"
                >
                  {/* Error title */}
                  <div>
                    <p
                      className="text-zinc-100 text-sm font-medium leading-snug group-hover:text-white transition-colors line-clamp-2"
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                    >
                      {err.error_message.slice(0, 80)}
                    </p>
                  </div>

                  {/* Preview */}
                  {preview && (
                    <p className="text-zinc-500 text-xs leading-5 line-clamp-3 flex-1">
                      {preview}…
                    </p>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${langColor}`}>
                      {displayName}
                    </span>
                    <div className="flex items-center gap-3">
                      {popularity ? (
                        <span className="text-[10px] uppercase tracking-wide text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-2 py-0.5">
                          {popularity}
                        </span>
                      ) : (
                        <span className="text-xs text-zinc-600">Guide</span>
                      )}
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="text-zinc-700 group-hover:text-cyan-400 transition-colors"
                      >
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            {currentPage > 1 && (
              <Link
                href={buildUrl({ page: currentPage - 1 })}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200 text-sm transition-all"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                Previous
              </Link>
            )}

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 7) {
                  pageNum = i + 1;
                } else if (currentPage <= 4) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 3) {
                  pageNum = totalPages - 6 + i;
                } else {
                  pageNum = currentPage - 3 + i;
                }
                return (
                  <Link
                    key={pageNum}
                    href={buildUrl({ page: pageNum })}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm transition-all ${
                      pageNum === currentPage
                        ? "bg-cyan-500 text-zinc-950 font-semibold" :"border border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
                    }`}
                  >
                    {pageNum}
                  </Link>
                );
              })}
            </div>

            {currentPage < totalPages && (
              <Link
                href={buildUrl({ page: currentPage + 1 })}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200 text-sm transition-all"
              >
                Next
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            )}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-zinc-900 border border-zinc-800 rounded-2xl p-6 text-center">
          <p className="text-zinc-400 text-sm mb-3">Have a {displayName} error that's not listed? Get an instant explanation.</p>
          <Link
            href="/error-explainer-tool"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500 text-zinc-950 font-semibold text-sm hover:bg-cyan-400 transition-colors"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            Explain Another Error
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-6 mt-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center text-zinc-600 text-xs">
          <p>DevError Explainer — A self-growing developer knowledge base</p>
        </div>
      </footer>
    </div>
  );
}
